import { AlertTriangle, CheckCircle2, Inbox, LoaderCircle } from "lucide-react";
import type { PropsWithChildren, ReactNode } from "react";

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-header-copy">
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Loading({
  compact = false,
  label = "Loading…",
}: {
  compact?: boolean;
  label?: string;
}) {
  return (
    <div
      className={`loading ${compact ? "compact" : ""}`}
      role="status"
      aria-live="polite"
    >
      <LoaderCircle className="spin" size={20} />
      <span>{label}</span>
    </div>
  );
}

export function ErrorNotice({ error }: { error: Error }) {
  return (
    <div className="notice error" role="alert">
      <AlertTriangle size={17} />
      <span>{error.message}</span>
    </div>
  );
}

export function PageError({
  error,
  onRetry,
  retrying = false,
}: {
  error: Error;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  return (
    <div className="page-error" role="alert">
      <span className="page-error-icon">
        <AlertTriangle size={22} />
      </span>
      <h1>We couldn’t load this page</h1>
      <p>{error.message}</p>
      {onRetry && (
        <button className="btn-primary" disabled={retrying} onClick={onRetry}>
          {retrying ? <ButtonLoader label="Trying again…" /> : "Try again"}
        </button>
      )}
    </div>
  );
}

export function SuccessNotice({ children }: { children: ReactNode }) {
  return (
    <div className="notice success" role="status">
      <CheckCircle2 size={17} />
      <span>{children}</span>
    </div>
  );
}

export function FieldError({
  id,
  children,
}: {
  id?: string;
  children?: ReactNode;
}) {
  if (!children) return null;
  return (
    <small className="field-error" id={id} role="alert">
      <AlertTriangle size={12} /> {children}
    </small>
  );
}

export function ButtonLoader({ label }: { label: string }) {
  return (
    <>
      <LoaderCircle className="spin" size={14} />
      <span>{label}</span>
    </>
  );
}

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="empty-state">
      <Inbox size={25} />
      <strong>{title}</strong>
      {description && <p>{description}</p>}
    </div>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <span className={`skeleton ${className}`} aria-hidden="true" />;
}

export function PageSkeleton({
  variant = "cards",
}: {
  variant?: "cards" | "table" | "detail" | "form";
}) {
  return (
    <div
      className={`page scroll-page page-skeleton skeleton-${variant}`}
      role="status"
      aria-label="Loading page"
    >
      <header className="page-header skeleton-header">
        <div>
          <Skeleton className="sk-title" />
          <Skeleton className="sk-copy" />
        </div>
      </header>
      {variant === "detail" ? (
        <div className="detail-grid">
          <aside>
            <SkeletonCard rows={6} />
          </aside>
          <div className="detail-content">
            <SkeletonCard rows={3} />
            <SkeletonCard rows={5} />
          </div>
        </div>
      ) : variant === "form" ? (
        <div className="intake-layout">
          <div className="form-sections">
            <SkeletonCard rows={5} />
            <SkeletonCard rows={5} />
          </div>
          <aside>
            <SkeletonCard rows={6} />
          </aside>
        </div>
      ) : (
        <>
          <div className="kpi-bar skeleton-kpis">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton className="sk-kpi" key={index} />
            ))}
          </div>
          <div className="content-grid">
            <SkeletonCard rows={variant === "table" ? 8 : 5} />
            <SkeletonCard rows={5} />
          </div>
        </>
      )}
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function SkeletonCard({ rows = 4 }: { rows?: number }) {
  return (
    <div className="skeleton-card">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton
          className={index === 0 ? "sk-line sk-line-title" : "sk-line"}
          key={index}
        />
      ))}
    </div>
  );
}

export function Card({
  children,
  className = "",
}: PropsWithChildren<{ className?: string }>) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Kpi({
  label,
  value,
  detail,
  tone = "navy",
}: {
  label: string;
  value: ReactNode;
  detail?: string;
  tone?: string;
}) {
  return (
    <div className="kpi-card">
      <span className="kpi-label">{label}</span>
      <strong className={`kpi-value c-${tone}`}>{value}</strong>
      {detail && <span className="kpi-sub">{detail}</span>}
    </div>
  );
}
